import React, { useState, useEffect } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Code, Github, Mail, Linkedin } from 'lucide-react';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Header component with navigation and theme toggle
 * Features smooth scrolling navigation and mobile menu
 */

const HeaderBase = styled(motion.header)<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  backdrop-filter: blur(20px);
  background-color: ${props => props.$scrolled 
    ? 'rgba(255, 255, 255, 0.9)' 
    : 'rgba(255, 255, 255, 0.8)'};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  font-family: ${props => props.theme.typography.fonts.heading};
  font-size: ${props => props.theme.typography.sizes.xl};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  cursor: pointer;
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(motion.a)`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  position: relative;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    color: ${props => props.theme.colors.primary[600]};
    background-color: ${props => props.theme.colors.surface};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: ${props => props.theme.gradients.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    transition: width ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  }

  &:hover::after {
    width: 100%;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    background-color: ${props => props.theme.colors.primary[600]};
    color: white;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.colors.surface};
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.text};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  backdrop-filter: blur(20px);
  padding: ${props => props.theme.spacing[6]} ${props => props.theme.spacing[4]};
`;

const MobileNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
`;

const MobileNavLink = styled(motion.a)`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  padding: ${props => props.theme.spacing[3]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};

  &:hover {
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const MobileSocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[6]};
`;

// Navigation items will be translated dynamically
const navItems = [
  { label: 'nav.about', href: '#about' },
  { label: 'nav.projects', href: '#projects' },
  { label: 'nav.tools', href: '#tools' },
  { label: 'nav.resources', href: '#resources' },
  { label: 'nav.contact', href: '#contact' },
];

const socialLinks = [
  { icon: Github, href: 'https://github.com/adda-neb', label: 'GitHub' },
  { icon: Mail, href: 'mailto:n.benadda.dev@gmail.com', label: 'Email' },
  { icon: Linkedin, href: 'https://linkedin.com/in/nadir-benadda-7741b1271/', label: 'LinkedIn' },
];

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <HeaderBase
        as="header"
        role="banner"
        $scrolled={scrolled}
        initial={{ y: prefersReducedMotion ? 0 : -100 }}
        animate={{ y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.6, ease: 'easeOut' }}
      >
        <Container>
          <Logo
            as="button"
            onClick={() => scrollToSection('#hero')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollToSection('#hero');
              }
            }}
            tabIndex={0}
            aria-label={t('nav.home') || 'Retour à l\'accueil'}
            whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            <LogoIcon>
              <Code size={24} />
            </LogoIcon>
            Web Dev
          </Logo>

          <Nav role="navigation" aria-label="Navigation principale">
            {navItems.map((item, index) => (
              <NavLink
                key={item.label}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }
                }}
                href={item.href}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.6, delay: prefersReducedMotion ? 0 : index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { y: -2 }}
              >
                {t(item.label)}
              </NavLink>
            ))}
          </Nav>

          <LanguageSwitcher />

          <SocialLinks role="navigation" aria-label="Réseaux sociaux">
            {socialLinks.map((social, index) => (
              <SocialLink
                key={social.label}
                href={social.href}
                target={social.href.startsWith('mailto:') ? undefined : '_blank'}
                rel={social.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                aria-label={social.label}
                initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.6, delay: prefersReducedMotion ? 0 : 0.5 + index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.9 }}
              >
                <social.icon size={20} aria-hidden="true" />
              </SocialLink>
            ))}
          </SocialLinks>

          <MobileMenuButton
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            whileTap={prefersReducedMotion ? {} : { scale: 0.9 }}
          >
            {mobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </MobileMenuButton>
        </Container>
      </HeaderBase>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            id="mobile-menu"
            role="navigation"
            aria-label="Navigation mobile"
            initial={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
          >
            <MobileNav>
              {navItems.map((item, index) => (
                <MobileNavLink
                  key={item.label}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      scrollToSection(item.href);
                    }
                  }}
                  href={item.href}
                  initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.3, delay: prefersReducedMotion ? 0 : index * 0.1 }}
                >
                  {t(item.label)}
                </MobileNavLink>
              ))}
            </MobileNav>

            <MobileSocialLinks>
              {socialLinks.map((social, index) => (
                <SocialLink
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('mailto:') ? undefined : '_blank'}
                  rel={social.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                  aria-label={social.label}
                  initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.3, delay: prefersReducedMotion ? 0 : 0.3 + index * 0.1 }}
                >
                  <social.icon size={20} aria-hidden="true" />
                </SocialLink>
              ))}
            </MobileSocialLinks>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
